import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { CampoDeFecha } from '../../../nucleo/fecha/CampoDeFecha.jsx';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CampoDeImporte } from '../../../nucleo/importes/CampoDeImporte.jsx';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import { consultarCuentas, PROPOSITOS } from '../../cuentas/api/cuentasApi.js';
import { ComboboxDeCuenta } from '../../movimientos/componentes/ComboboxDeCuenta.jsx';
import { ContadorDeMensualidad, LeyendaDeContadores } from '../../deuda-descompuesta/componentes/ContadorDeMensualidad.jsx';
import { generarPlanEnCurso, simularPlanEnCurso } from '../api/msiApi.js';
import { CalendarioPlegadoPorAnio } from '../componentes/CalendarioPlegadoPorAnio.jsx';
import { importe as formatearImporte, MESES_FRECUENTES, MESES_MAXIMO, MESES_MINIMO } from '../formato.js';

/**
 * Los renglones que el pliegue necesita, con lo que ya vino resuelto del servidor
 * (RNF-038, RNF-059): aquí no se cuenta ningún corte y no se compara ninguna fecha.
 *
 * RN-772 · «cargada» incluye a la que el banco ya cargó antes de usar la aplicación
 * —lo está por construcción— y a la que ya pasó su corte dentro de la aplicación.
 * La que sigue es la que el servidor señala con `siguienteMensualidad`.
 */
function renglonesDeLaVistaPrevia(vistaPrevia) {
  return vistaPrevia.mensualidades.map((mensualidad) => ({
    clave: mensualidad.numeroDeMensualidad,
    fechaCorte: mensualidad.fechaCorte,
    importe: mensualidad.importe,
    estaCargada: mensualidad.estadoPresentado !== 'PENDIENTE',
    esHistorica: Boolean(mensualidad.esHistorica),
    esLaQueSigue: vistaPrevia.siguienteMensualidad === mensualidad.numeroDeMensualidad,
    esLaDelResiduo: Boolean(mensualidad.esLaQueAbsorbeElResiduo),
    origen: mensualidad,
  }));
}

/**
 * UNA COMPRA A MESES QUE EMPEZÓ ANTES (RF-255, RF-256; prototipo v42
 * `-plan-en-curso-generar`).
 *
 * AQUÍ NO SE CAPTURA NINGÚN GASTO, y es la regla dura de alcance de este
 * incremento: capturar el gasto con fecha vieja y generarle plan normal está
 * PROHIBIDO por Deisy Paez, palabras literales «duplica deuda y ensucia meses
 * que no registro» (RN-711). Esta pantalla no tiene ni puede tener una vía que
 * lo haga: registra el plan sobre la TARJETA, con su fecha de compra declarada.
 *
 * LA APLICACIÓN PROPONE Y Deisy Paez CORRIGE (D-406, palabras literales: «que la
 * app las proponga y yo corrijo»). El número de mensualidades ya cargadas lo
 * propone el servidor contando los cortes ocurridos desde la compra; ella lo
 * corrige SIEMPRE, el campo nunca se bloquea, y 0 es un número válido (D-416).
 * Cuando la propuesta no se puede derivar, el campo abre vacío y es obligatorio,
 * pero el alta NO se bloquea (RN-724b).
 *
 * LA VISTA PREVIA LA CALCULA EL SERVIDOR, con la MISMA pieza con la que guardará
 * (D-413, patrón D-78): esta pantalla no reparte importes, no cuenta cortes y no
 * compara fechas (RNF-038, RNF-059). Avisa en los dos bordes de la frontera
 * —RN-717 y RN-731— y ninguno bloquea: manda el estado de cuenta.
 *
 * EL DESCUENTO DE LO DECLARADO (D-418, RN-729) es la pieza que impide que el
 * mismo dinero se reste dos veces: lo que esta compra respalda SALE de lo que
 * Deisy Paez declaró a mano, en el mismo acto, con la cifra a la vista y
 * editable. Dejarlo en 0 es válido y la comparación lo denuncia después.
 */
export default function GenerarPlanEnCurso() {
  const navegar = useNavigate();
  const [parametros] = useSearchParams();
  const hoy = obtenerFechaDeNegocioHoy();

  const [tarjetas, setTarjetas] = useState([]);
  const [cargandoTarjetas, setCargandoTarjetas] = useState(true);

  const [idCuenta, setIdCuenta] = useState(null);
  const [fechaDeCompra, setFechaDeCompra] = useState('');
  const [importeTotal, setImporteTotal] = useState('');
  const [meses, setMeses] = useState('');
  const [cargadas, setCargadas] = useState('');
  const [descuento, setDescuento] = useState('');

  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  /*
   * ¿YA CORRIGIÓ A MANO? Mientras no haya tocado el campo, cada recálculo lo
   * vuelve a poner con lo que el servidor propone. En cuanto lo corrige, SU
   * NÚMERO MANDA y ningún recálculo se lo pisa: eso es lo que significa «yo
   * corrijo» (D-406). Para volver a la propuesta hay un enlace explícito.
   */
  const corrigioCargadas = useRef(false);
  const corrigioDescuento = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        // Solo tarjetas de crédito activas y propias: el plan en curso lo opera su
        // propietario (RN-725, RN-712e).
        const respuesta = await consultarCuentas({ proposito: PROPOSITOS.capturarOrigen });
        const soloCredito = respuesta.cuentas.filter((c) => c.esCredito);
        setTarjetas(soloCredito);

        const sugerida = Number(parametros.get('idCuenta'));
        if (sugerida && soloCredito.some((c) => c.idCuenta === sugerida)) setIdCuenta(sugerida);
      } catch (fallo) {
        setError(fallo);
      } finally {
        setCargandoTarjetas(false);
      }
    })();
  }, [parametros]);

  const tarjeta = tarjetas.find((c) => c.idCuenta === idCuenta) ?? null;

  const datosCompletos = Boolean(idCuenta) && Boolean(fechaDeCompra)
    && Number(importeTotal) > 0 && Number(meses) >= MESES_MINIMO;

  /**
   * LA VISTA PREVIA, PEDIDA AL SERVIDOR (D-413). Se vuelve a pedir cada vez que
   * cambia un dato que la mueve: así la cifra que se enseña y la que se guardará
   * no pueden diferir, porque las calcula la misma pieza.
   */
  const recalcular = useCallback(async () => {
    if (!datosCompletos) { setVistaPrevia(null); return; }

    setError(null);
    try {
      const previa = await simularPlanEnCurso({
        idCuenta,
        fechaDeCompra,
        importeTotal: String(importeTotal),
        numeroDeMeses: Number(meses),
        mensualidadesCargadas: corrigioCargadas.current && cargadas !== '' ? Number(cargadas) : null,
        importeADescontarDeDiferidoDeclarado:
          corrigioDescuento.current && descuento !== '' ? String(descuento) : null,
        fechaLocal: hoy,
      });

      setVistaPrevia(previa);

      // La propuesta solo se adopta mientras no haya corregido a mano.
      if (!corrigioCargadas.current) {
        setCargadas(previa.mensualidadesCargadasPropuestas === null
          ? ''
          : String(previa.mensualidadesCargadasPropuestas));
      }
      if (!corrigioDescuento.current) setDescuento(previa.importeDescuentoPropuesto);
    } catch (fallo) {
      setVistaPrevia(null);
      setError(fallo);
    }
  }, [datosCompletos, idCuenta, fechaDeCompra, importeTotal, meses, cargadas, descuento, hoy]);

  useEffect(() => { recalcular(); }, [recalcular]);

  function corregirCargadas(valor) {
    corrigioCargadas.current = true;
    setCargadas(valor);
  }

  function volverALaPropuestaDeCargadas() {
    corrigioCargadas.current = false;
    setCargadas(vistaPrevia?.mensualidadesCargadasPropuestas === null || vistaPrevia === null
      ? ''
      : String(vistaPrevia.mensualidadesCargadasPropuestas));
  }

  function corregirDescuento(valor) {
    corrigioDescuento.current = true;
    setDescuento(valor);
  }

  function volverALaPropuestaDeDescuento() {
    corrigioDescuento.current = false;
    if (vistaPrevia) setDescuento(vistaPrevia.importeDescuentoPropuesto);
  }

  async function registrar() {
    setError(null);
    setGuardando(true);
    try {
      const plan = await generarPlanEnCurso({
        idCuenta,
        fechaDeCompra,
        importeTotal: String(importeTotal),
        numeroDeMeses: Number(meses),
        mensualidadesCargadas: Number(cargadas),
        importeADescontarDeDiferidoDeclarado: descuento === '' ? null : String(descuento),
        fechaLocal: hoy,
      });
      navegar(`/cuentas/meses/planes/${plan.idPlan}`, { replace: true });
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Shell titulo="Una compra a meses que empezó antes" volverA="/cuentas/vencimientos/comparacion">
      <CintaDeVersionNueva />

      <div className="aviso i" role="status" data-prueba="en-curso-para-que-sirve">
        <b className="tit">Para qué sirve esto</b>
        Si traes una compra a meses de antes de usar la aplicación, aquí la registras tal como va: en
        qué tarjeta es, de qué fecha, su total, de cuántos meses y cuántas mensualidades ya te cargó el
        banco. De la siguiente en adelante se comporta como cualquier compra a meses: aparece en lo que
        vence, imputa su mensualidad al mes y lleva su contador.
      </div>

      <AvisoDeError error={error} />

      <div className="card" style={{ margin: 12 }}>
        <div className="campo">
          <label htmlFor="idCuenta">¿EN QUÉ TARJETA?</label>
          <ComboboxDeCuenta
            id="idCuenta"
            cuentas={tarjetas}
            cargando={cargandoTarjetas}
            valor={idCuenta}
            alCambiar={setIdCuenta}
            textoDePeticion="Elige la tarjeta…"
          />
          <span className="ayuda">Solo tus tarjetas de crédito activas.</span>
        </div>

        <div className="campo">
          <label htmlFor="fechaDeCompra">FECHA DE LA COMPRA</label>
          <CampoDeFecha
            id="fechaDeCompra"
            name="fechaDeCompra"
            valor={fechaDeCompra}
            alCambiar={setFechaDeCompra}
            max={hoy}
            datoDePrueba="en-curso-campo-fecha"
          />
          {/* Literal asentado en RN-766 con el prototipo aprobado v45. Es correcto y
              no contradice el techo derivado: como no se admite fecha futura (D-45),
              el techo jamás restringe la fecha de una compra, y el rango efectivo
              del campo es exactamente este. */}
          <span className="ayuda">Desde el 01/01/2025 y anterior a hoy.</span>
        </div>

        <div className="campo">
          <label htmlFor="importeTotal">TOTAL DE LA COMPRA</label>
          {/*
            RNF-133 (heredada del 18, sin reinterpretar): el importe conserva lo
            tecleado y queda SELECCIONADO COMPLETO al tomar el foco, así que un
            tecleo lo reemplaza entero. Se usa el CAMPO ÚNICO del núcleo y no se
            le pasa ninguna propiedad nueva: el componente sigue congelado tal
            cual (RN-427, D-229, D-241) y jamás se deshabilita.
          */}
          <CampoDeImporte
            id="importeTotal"
            valor={importeTotal}
            alCambiar={setImporteTotal}
            codigoDivisa={tarjeta?.codigoISO}
            contexto="el total de la compra"
            data-prueba="en-curso-campo-total"
          />
          <span className="ayuda">Es el precio completo de la compra, no la mensualidad.</span>
        </div>

        <div className="campo">
          <label htmlFor="meses">¿DE CUÁNTOS MESES?</label>
          <div data-prueba="en-curso-atajos-de-meses">
            {MESES_FRECUENTES.map((cantidad) => (
              <button
                key={cantidad}
                className="btn sec"
                type="button"
                aria-pressed={Number(meses) === cantidad}
                onClick={() => setMeses(String(cantidad))}
              >
                {cantidad}
              </button>
            ))}
          </div>
          <input
            id="meses"
            name="meses"
            type="number"
            min={MESES_MINIMO}
            max={MESES_MAXIMO}
            value={meses}
            onChange={(evento) => setMeses(evento.target.value)}
          />
          <span className="ayuda">De {MESES_MINIMO} a {MESES_MAXIMO}.</span>
        </div>

        <div className="campo">
          <label htmlFor="cargadas">¿CUÁNTAS MENSUALIDADES YA TE CARGARON?</label>
          <input
            id="cargadas"
            name="cargadas"
            type="number"
            min={0}
            value={cargadas}
            onChange={(evento) => corregirCargadas(evento.target.value)}
            data-prueba="en-curso-campo-cargadas"
          />

          {/* El aviso de que no se pudo proponer lo redacta el servidor (RN-724b):
              aquí no hay redacción propia. El campo sigue abierto y el registro
              sigue disponible. */}
          {vistaPrevia?.textoAvisoDePropuestaNoDerivable && (
            <div className="aviso w" role="status" data-prueba="en-curso-sin-propuesta">
              ⚠ {vistaPrevia.textoAvisoDePropuestaNoDerivable}
            </div>
          )}

          {vistaPrevia && vistaPrevia.mensualidadesCargadasPropuestas !== null && (
            <span className="ayuda" data-prueba="en-curso-propuesta">
              La aplicación propone <b>{vistaPrevia.mensualidadesCargadasPropuestas}</b>, contando los
              cortes que ya pasaron desde la fecha de la compra. Corrígelo si tu estado de cuenta dice
              otra cosa; 0 también es un número válido.
              {String(vistaPrevia.mensualidadesCargadasPropuestas) !== String(cargadas) && (
                <>
                  {' '}
                  <button
                    className="btn sec" style={{ padding: '2px 8px', fontSize: 12 }}
                    type="button"
                    onClick={volverALaPropuestaDeCargadas}
                    data-prueba="en-curso-volver-a-propuesta"
                  >
                    Volver a {vistaPrevia.mensualidadesCargadasPropuestas}
                  </button>
                </>
              )}
            </span>
          )}
        </div>
      </div>

      {/* ===== EL BLOQUE DE DESCUENTO DE LO DECLARADO (D-418, RN-729, RN-733b) ==
          Los literales son REQUISITO y los arma el servidor (RN-733): esta
          pantalla los pinta tal cual y no los varía. */}
      {vistaPrevia && (
        <div className="card" style={{ margin: 12 }} data-prueba="en-curso-bloque-descuento">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>
            {vistaPrevia.tituloDelBloqueDeDescuento.toUpperCase()}
          </h4>
          <div className="ayuda" data-prueba="en-curso-descuento-texto">
            {vistaPrevia.textoDelBloqueDeDescuento}
          </div>

          {vistaPrevia.hayDeDondeDescontar && (
            <>
              <div className="campo">
                <label htmlFor="descuento">¿CUÁNTO SE DESCUENTA DE LO QUE DECLARASTE?</label>
                <CampoDeImporte
                  id="descuento"
                  valor={descuento}
                  alCambiar={corregirDescuento}
                  codigoDivisa={vistaPrevia.codigoISO}
                  contexto="el descuento de lo declarado"
                  data-prueba="en-curso-campo-descuento"
                />
                <span className="ayuda">
                  Viene puesto con lo que esta compra respalda. Puedes bajarlo, y 0 es un número
                  válido: si lo dejas en 0, ese dinero seguirá contado dos veces hasta que lo
                  descuentes desde la comparación.
                  {vistaPrevia.importeDescuentoPropuesto !== String(descuento) && (
                    <>
                      {' '}
                      <button
                        className="btn sec" style={{ padding: '2px 8px', fontSize: 12 }}
                        type="button"
                        onClick={volverALaPropuestaDeDescuento}
                        data-prueba="en-curso-volver-a-descuento"
                      >
                        Volver a {formatearImporte(vistaPrevia.importeDescuentoPropuesto, vistaPrevia.codigoISO)}
                      </button>
                    </>
                  )}
                </span>
              </div>

              <div className="fila" data-prueba="en-curso-declarado-antes">
                <span className="crece">Declarado ahora</span>
                <b>{formatearImporte(vistaPrevia.importeDiferidoDeclaradoVigente, vistaPrevia.codigoISO)}</b>
              </div>
              <div className="fila" data-prueba="en-curso-declarado-despues">
                <span className="crece">Al guardar</span>
                <b>{formatearImporte(vistaPrevia.importeDeclaradoAlGuardar, vistaPrevia.codigoISO)}</b>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== LA VISTA PREVIA DEL CALENDARIO (RF-256) ========================= */}
      {vistaPrevia && (
        <div className="card" style={{ margin: 12 }} data-prueba="en-curso-vista-previa">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>ASÍ VA A QUEDAR</h4>

          <div className="fila">
            <span className="crece">Se registra como</span>
            <b data-prueba="en-curso-identificacion">{vistaPrevia.textoIdentificacion}</b>
          </div>
          <div className="fila">
            <span className="crece">Mensualidades que ya te cargaron</span>
            <b data-prueba="en-curso-cuantas-historicas">{vistaPrevia.cantidadDeHistoricas}</b>
          </div>
          {vistaPrevia.siguienteMensualidad && (
            <div className="fila">
              <span className="crece">La siguiente por pagar</span>
              <b>
                {vistaPrevia.siguienteMensualidad} de {vistaPrevia.numeroDeMeses} · corte del{' '}
                {formatearFecha(vistaPrevia.fechaCorteDeLaSiguiente)}
              </b>
            </div>
          )}
          {vistaPrevia.hayDeDondeDescontar && (
            <>
              <div className="fila" data-prueba="en-curso-sale-de-lo-declarado">
                <span className="crece">Sale de lo que declaraste — lo que esta compra respalda</span>
                <b>−{formatearImporte(descuento || '0.00', vistaPrevia.codigoISO)}</b>
              </div>
              <div className="fila">
                <span className="crece">Lo declarado queda en</span>
                <b>{formatearImporte(vistaPrevia.importeDeclaradoAlGuardar, vistaPrevia.codigoISO)}</b>
              </div>
            </>
          )}

          {/* EL AVISO DE FRONTERA LO REDACTA EL SERVIDOR (RN-717, RN-731) y NO
              BLOQUEA: registrar sigue disponible. */}
          {vistaPrevia.textoAvisoDeFrontera && (
            <div className="aviso w" role="status" data-prueba="en-curso-aviso-frontera">
              ⚠ {vistaPrevia.textoAvisoDeFrontera}
            </div>
          )}

          {/* PLIEGUE POR AÑO EN LOS PLAZOS LARGOS (RF-274, RN-767, RN-772). Con 24
              mensualidades o menos la vista previa se ve igual que siempre. En el
              resumen de cada año las históricas cuentan JUNTO con las cargadas; la
              distinción no se pierde: al abrir el año cada renglón la pinta con la
              pieza compartida del contador. */}
          <CalendarioPlegadoPorAnio
            renglones={renglonesDeLaVistaPrevia(vistaPrevia)}
            codigoISO={vistaPrevia.codigoISO}
            datoDePrueba="en-curso-vista-previa-lista"
            pintarRenglon={(renglon) => {
              const mensualidad = renglon.origen;

              return (
                <div className="fila" key={mensualidad.numeroDeMensualidad}>
                  <div className="crece">
                    {/* PIEZA COMPARTIDA del contador (RN-718, RNF-132): la distinción
                        histórica la pinta ella con las cuatro señales de RN-707.
                        Ninguna pantalla vuelve a dibujar un contador a mano. */}
                    <ContadorDeMensualidad
                      contador={mensualidad.contadorDeMensualidad}
                      esHistorica={mensualidad.esHistorica}
                      datoDePrueba={`en-curso-contador-${mensualidad.numeroDeMensualidad}`}
                    />
                    <small>
                      corte {formatearFecha(mensualidad.fechaCorte)} · pago a más tardar el{' '}
                      {formatearFecha(mensualidad.fechaLimitePago)}
                    </small>
                  </div>
                  <span className="imp">
                    {formatearImporte(mensualidad.importe, vistaPrevia.codigoISO)}
                    {mensualidad.esLaQueAbsorbeElResiduo && (
                      <small data-prueba="en-curso-absorbe-residuo">lleva los centavos que sobran</small>
                    )}
                  </span>
                </div>
              );
            }}
          />

          <LeyendaDeContadores hayHistoricas={vistaPrevia.cantidadDeHistoricas > 0} />

          <button
            className="btn ancho"
            type="button"
            disabled={guardando || !datosCompletos || cargadas === ''}
            onClick={registrar}
            data-prueba="en-curso-registrar"
          >
            Registrar la compra a meses
          </button>
        </div>
      )}

      <div className="card" style={{ margin: 12 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>ESTO NO ES LA CAPTURA DE UN GASTO</h4>
        <span className="ayuda" data-prueba="en-curso-no-es-un-gasto">
          Aquí no se registra ningún movimiento: el dinero de esta compra ya está dentro de la deuda con
          la que empezó tu tarjeta. Capturarla otra vez como gasto te duplicaría la deuda y te movería
          meses que no llevas en la aplicación. Las mensualidades que ya te cargaron quedan marcadas
          como tales: no suman deuda y no cuentan como gasto de esos meses. <b>Tu deuda total es la
          misma antes y después de registrarla.</b>
        </span>
      </div>

      <div className="card" style={{ margin: 12 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>ASÍ VAS A RECONOCERLA</h4>
        <span className="ayuda" data-prueba="en-curso-como-reconocerla">
          Como esta compra no se captura como gasto, en los listados aparece por su fecha y su total.
          El total al lado es lo que te permite distinguir dos compras del mismo día.
        </span>
      </div>

      <div className="card" style={{ margin: 12 }}>
        <button
          className="btn sec ancho"
          type="button"
          onClick={() => navegar('/cuentas/vencimientos/comparacion', { replace: true })}
        >
          Cancelar
        </button>
      </div>
    </Shell>
  );
}
